<template>
  <CRow
    role="radiogroup"
    aria-label="Mark this pass as"
    class="tardy-radio-group"
  >
    <button
      class="box"
      :class="{
        active: state.active ? state.active === option.value : ''
      }"
      v-for="(option, index) in allowedOptions"
      :key="option.value || index"
      type="button"
      role="radio"
      :aria-checked="String(state.active === option.value)"
      :aria-disabled="String(disabled)"
      :disabled="disabled"
      :tabindex="
        state.active
          ? state.active === option.value
            ? 0
            : -1
          : index === 0
            ? 0
            : -1
      "
      :ref="(el) => setOptionRef(el, option.value)"
      @click="checkModel(option.value)"
      @keydown="onOptionKeydown($event, index)"
    >
      <div>
        <i aria-hidden="true" :class="option.icon"></i>
        <span class="ms-2">{{ option.label }}</span>
      </div>
    </button>
  </CRow>
</template>

<script>
import { useI18n } from "vue-i18n"
import { reactive, computed, watch, onMounted, ref, nextTick } from "vue"

export default {
  name: "TardyRadioGroup",
  props: {
    value: {},
    modelValue: {},
    isAllowedLates: {
      type: Boolean,
      default: false
    },
    allowedLates: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autoSelectSingleAllowedOption: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue", "input"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const optionRefs = ref({})
    const state = reactive({
      model: props.modelValue,
      active: props.modelValue ? props.modelValue : ""
    })

    const allowedOptions = computed(() => {
      return !props.isAllowedLates
        ? repeatOptions.value
        : props.allowedLates.ltc && props.allowedLates.lts
          ? repeatOptions.value
          : props.allowedLates.lts
            ? [repeatOptions.value[0]]
            : props.allowedLates.ltc
              ? [repeatOptions.value[1]]
              : []
    })

    const repeatOptions = computed(() => {
      return [
        {
          icon: "ri-rest-time-line",
          label: t("common.late_to_school"),
          value: "LTS"
        },
        {
          icon: "ri-alarm-line",
          label: t("common.late_to_class"),
          value: "LTC"
        }
      ]
    })

    const changeModel = (event) => {
      state.model = event
      emit("input", state.model)
      emit("update:modelValue", state.model)
    }

    const checkModel = (value) => {
      if (props.disabled) return
      state.active = value === state.active ? "" : value
      changeModel(state.active)
    }

    const setOptionRef = (el, optionValue) => {
      if (!optionValue) return
      if (el) {
        optionRefs.value[optionValue] = el
        return
      }
      delete optionRefs.value[optionValue]
    }

    const focusOptionAt = (index) => {
      const optionValue = allowedOptions.value[index]?.value
      if (!optionValue) return
      optionRefs.value[optionValue]?.focus?.()
    }

    const onOptionKeydown = (event, index) => {
      if (props.disabled || !allowedOptions.value.length) return
      const key = event.key
      const lastIndex = allowedOptions.value.length - 1

      const moveAndSelect = (nextIndex) => {
        const nextOption = allowedOptions.value[nextIndex]
        if (!nextOption) return
        state.active = nextOption.value
        changeModel(nextOption.value)
        nextTick(() => {
          focusOptionAt(nextIndex)
        })
      }

      if (key === "ArrowRight" || key === "ArrowDown") {
        event.preventDefault()
        const nextIndex = index >= lastIndex ? 0 : index + 1
        moveAndSelect(nextIndex)
        return
      }

      if (key === "ArrowLeft" || key === "ArrowUp") {
        event.preventDefault()
        const nextIndex = index <= 0 ? lastIndex : index - 1
        moveAndSelect(nextIndex)
        return
      }

      if (key === "Home") {
        event.preventDefault()
        moveAndSelect(0)
        return
      }

      if (key === "End") {
        event.preventDefault()
        moveAndSelect(lastIndex)
        return
      }

      if (key === " " || key === "Enter") {
        event.preventDefault()
        const currentOption = allowedOptions.value[index]
        if (currentOption) {
          checkModel(currentOption.value)
        }
      }
    }

    onMounted(() => {
      if (props.value) {
        checkModel(props.value)
      }
    })

    watch(
      allowedOptions,
      (newOptions) => {
        const allowedValues = newOptions.map((option) => option.value)
        Object.keys(optionRefs.value).forEach((value) => {
          if (!allowedValues.includes(value)) {
            delete optionRefs.value[value]
          }
        })

        const optionValues = newOptions.map((option) => option.value)
        const hasActiveValue = optionValues.includes(state.active)

        if (hasActiveValue) {
          changeModel(state.active)
          return
        }

        if (
          newOptions.length === 1 &&
          props.autoSelectSingleAllowedOption &&
          props.isAllowedLates
        ) {
          const singleOptionValue = newOptions[0].value
          state.active = singleOptionValue
          changeModel(singleOptionValue)
        } else {
          state.active = ""
          changeModel(state.active)
        }
      },
      { immediate: true }
    )

    return {
      state,
      allowedOptions,
      props,
      changeModel,
      checkModel,
      setOptionRef,
      onOptionKeydown,
      t
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/assets/scss/_variables";
@import "src/assets/v3scss/_variables";
.box {
  display: flex;
  width: fit-content;
  cursor: pointer;
  background: transparent;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid $gray-lighten-20;
  color: $base-blue;
  margin-left: 12px;
}
.tardy-radio-group:focus-within {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px $outline-color;
}
.box:disabled {
  cursor: not-allowed;
}
.active {
  border: 1px solid $blue-darken-20;
  background: $blue-darken-20;
  color: $white;
}
</style>
